import { useState } from "react";
import { supabase } from "../supabaseClient";
import styles from "../styles/theme";

export default function JobDetail({ job, myProfile, user, onBack, accountType, navigate }) {
  const isEmployer = accountType === 'employer';
  const [applyView, setApplyView] = useState(false);
  const [applyForm, setApplyForm] = useState({ name: '', email: '', message: '' });
  const [applySubmitting, setApplySubmitting] = useState(false);
  const [applySuccess, setApplySuccess] = useState(false);
  const [applyError, setApplyError] = useState('');

  const handleApply = async () => {
    if (!applyForm.name || !applyForm.email) {
      setApplyError('Name and email are required.');
      return;
    }
    setApplySubmitting(true);
    setApplyError('');

    // Resolve tech_profile_id: use myProfile if available, otherwise look up by user_id
    let techProfileId = myProfile?.id || null;
    if (!techProfileId && user) {
      const { data: tp } = await supabase.from('tech_profiles')
        .select('id').eq('user_id', user.id).single();
      if (tp) techProfileId = tp.id;
    }

    const { error } = await supabase.from('applications').insert({
      job_id: job.id,
      applicant_name: applyForm.name,
      applicant_email: applyForm.email,
      message: applyForm.message,
      ...(techProfileId ? { tech_profile_id: techProfileId } : {}),
    });
    setApplySubmitting(false);
    if (error) setApplyError('Something went wrong. Please try again.');
    else {
      setApplySuccess(true);
      // Notify employer via email (fire and forget)
      supabase.functions.invoke('send-application-notification', {
        body: {
          job_id: job.id,
          applicant_name: applyForm.name,
          applicant_email: applyForm.email,
          message: applyForm.message,
          ...(techProfileId ? { tech_profile_id: techProfileId } : {}),
        },
      }).catch(() => {});
      // If tech was invited to this job, auto-accept the invitation
      if (techProfileId) {
        supabase.from('invitations')
          .update({ status: 'accepted' })
          .eq('tech_profile_id', techProfileId)
          .eq('job_post_id', job.id)
          .eq('status', 'pending')
          .then(() => {});
      }
    }
  };

  return (
    <div style={styles.detailWrap} data-detail-wrap>
      <button style={styles.backBtn} data-back-btn onClick={() => { onBack(); }}>
        {"\u2190"} Back to Jobs
      </button>
      <div style={styles.detailCard} data-detail-card>
        <div style={styles.detailHeader}>
          <div>
            <div style={styles.cardCompany}>{job.company}</div>
            <h2 style={styles.detailTitle}>{job.title}</h2>
            <div style={styles.cardMeta}>
              <span>{"\uD83D\uDCCD"} {job.location}</span>
              <span>{"\uD83D\uDCB0"} {job.salary}</span>
              <span>{"\uD83D\uDDC2"} {job.type}</span>
            </div>
          </div>
        </div>
        <div style={styles.divider} />
        <h3 style={styles.sectionLabel} data-section-label>Required Certifications</h3>
        <div style={styles.certTags} data-cert-tags>
          {job.requiredCerts.map(c => <span key={c} style={{ ...styles.certTag, background: "#8B0000" }} data-cert-tag>{c}</span>)}
        </div>
        {job.preferredCerts.length > 0 && (
          <>
            <h3 style={styles.sectionLabel} data-section-label>Preferred Certifications</h3>
            <div style={styles.certTags} data-cert-tags>
              {job.preferredCerts.map(c => <span key={c} style={{ ...styles.certTag, background: "#1a3a5a" }} data-cert-tag>{c}</span>)}
            </div>
          </>
        )}
        <h3 style={styles.sectionLabel} data-section-label>Job Description</h3>
        <p style={styles.detailDesc}>{job.description}</p>

        {/* Employer accounts cannot apply */}
        {isEmployer && (
          <div style={{ background: '#1a1408', border: '1px solid #d97706', borderRadius: 8, padding: '14px 18px', fontSize: 13, color: '#d97706' }}>
            Employer accounts cannot apply for jobs. This listing is for technician applicants.
            <button style={{ background: 'none', border: 'none', color: '#d97706', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, marginLeft: 8 }}
              onClick={() => navigate && navigate('/employer-dashboard')}>Go to Employer Hub</button>
          </div>
        )}

        {/* Unauthenticated users: prompt to sign up or log in */}
        {!user && !isEmployer && (
          <div style={{ background: '#111316', border: '1px solid #2a2c2e', borderRadius: 8, padding: '20px 24px', marginTop: 16, textAlign: 'center' }}>
            <p style={{ color: '#e8e4dc', fontSize: 15, fontWeight: 'bold', margin: '0 0 8px' }}>
              Create a free account to apply for this position
            </p>
            <p style={{ color: '#9a9490', fontSize: 13, margin: '0 0 16px' }}>
              Sign up to submit your application and track its status.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button style={{
                background: '#d97706', border: 'none', color: '#0d0f10', padding: '10px 20px',
                fontSize: 14, fontWeight: 'bold', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
              }} onClick={() => {
                sessionStorage.setItem('pendingJobId', job.id);
                navigate && navigate('/signup', { state: { accountType: 'technician' } });
              }}>
                Sign Up as Technician
              </button>
              <button style={{
                background: 'none', border: '1px solid #333', color: '#e8e4dc', padding: '10px 20px',
                fontSize: 14, borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
              }} onClick={() => {
                sessionStorage.setItem('pendingJobId', job.id);
                navigate && navigate('/login', { state: { returnTo: `/?job=${job.id}` } });
              }}>
                Log In
              </button>
            </div>
          </div>
        )}

        {user && !isEmployer && !applyView && !applySuccess && (() => {
          const hasName = myProfile && myProfile.name;
          const hasRole = myProfile && (
            (Array.isArray(myProfile.dodCerts) && myProfile.dodCerts.length > 0) ||
            (Array.isArray(myProfile.specificRoles) && myProfile.specificRoles.length > 0) ||
            myProfile.jobRolePreference === 'any'
          );
          if (!hasName || !hasRole) {
            const msg = !hasName && !hasRole
              ? 'To apply, please add your name and select at least one UXO role in your profile.'
              : !hasName
                ? 'To apply, please add your full name to your profile.'
                : 'To apply, please select at least one UXO role in your profile. Employers need to know your certification level to consider your application.';
            return (
              <div style={{ background: '#111316', border: '1px solid #2a2c2e', borderRadius: 8, padding: '20px 24px', marginTop: 16, textAlign: 'center' }}>
                <p style={{ color: '#9a9490', fontSize: 13, margin: '0 0 16px', lineHeight: 1.5 }}>
                  {msg}
                </p>
                <button style={{
                  background: '#d97706', border: 'none', color: '#0d0f10', padding: '10px 20px',
                  fontSize: 14, fontWeight: 'bold', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit',
                }} onClick={() => {
                  sessionStorage.setItem('pendingJobId', job.id);
                  navigate && navigate('/create-profile');
                }}>
                  Complete Profile
                </button>
              </div>
            );
          }
          return (
            <button style={styles.btnPrimary} data-btn-primary onClick={() => {
              setApplyForm({ name: myProfile?.name || '', email: myProfile?.email || user?.email || '', message: '' });
              setApplyError('');
              setApplyView(true);
            }}>Apply Now</button>
          );
        })()}

        {user && !isEmployer && applySuccess && (
          <div style={{ background: '#1a4a2e', border: '1px solid #4ade80', borderRadius: 8, padding: '16px 20px', color: '#4ade80', fontSize: 14 }}>
            {"\u2705"} Application submitted! The employer will review your application and reach out to you directly.
          </div>
        )}

        {user && !isEmployer && applyView && !applySuccess && (
          <div style={{ marginTop: 16, background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 8, padding: '20px 24px' }}>
            <h3 style={{ ...styles.sectionLabel, marginTop: 0 }}>Apply for {job.title}</h3>
            <div style={styles.formFields} data-form-fields>
              <label style={styles.label}>Your Name</label>
              <input style={styles.input} value={applyForm.name} onChange={e => setApplyForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" />
              <label style={styles.label}>Email Address</label>
              <input style={styles.input} type="email" value={applyForm.email} onChange={e => setApplyForm(f => ({ ...f, email: e.target.value }))} placeholder="you@email.com" />
              <label style={styles.label}>Cover Note (optional)</label>
              <textarea style={styles.textarea} value={applyForm.message} onChange={e => setApplyForm(f => ({ ...f, message: e.target.value }))} placeholder="Brief note about your background and interest..." />
              {applyError && <div style={styles.errorMsg}>{"\u26A0\uFE0F"} {applyError}</div>}
              <div style={styles.formRow} data-form-row>
                <button style={styles.btnSecondary} data-btn-secondary onClick={() => { setApplyView(false); setApplyError(''); }}>Cancel</button>
                <button style={styles.btnPrimary} data-btn-primary onClick={handleApply} disabled={applySubmitting}>
                  {applySubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
